import { Question, ApiRequestQuestion } from '../../pages/new-questions/types';
import request from '../../shared/request';
import { DISAGREE_API_MESSAGES } from './constants';

export const getAllQuestions = async (filters: {
  term?: string;
  families?: string;
  modules?: string;
  question_type?: string;
  page?: number;
}): Promise<ApiRequestQuestion | null> => {
  try {
    const response = await request(`/v2/students/questions`, {
      method: 'GET',
      qsParams: {
        term: filters.term,
        families: filters.families,
        module_id: filters.modules,
        question_type: filters.question_type,
        page: filters.page || 1,
      },
    });
    return response;
  } catch (error) {
    return null;
  }
};

export const getQuestionById = async (id: number): Promise<Question | null> => {
  const response = await request(`/v2/students/questions/${id}`, {
    method: 'GET',
  });
  return response || null;
};

// Interface para o resultado da avaliação
export interface EvaluationResult {
  score: number;
  maxScore: number;
  feedback: string;
  evaluatedAt: string;
  isCorrect?: boolean;
  verdict?: string;
  evaluationMethod?: string;
}

export interface DisagreeAiFeedbackPayload {
  questionId: number;
  provaId?: number;
  disagreementReason: string;
  studentAnswer?: string;
  evaluationResult?: EvaluationResult;
}

export interface DisagreeAiFeedbackResponse {
  success: boolean;
  message: string;
  alreadyExists?: boolean;
}

export const submitDisagreeAiFeedback = async (
  payload: DisagreeAiFeedbackPayload,
): Promise<DisagreeAiFeedbackResponse> => {
  if (!payload.provaId && (!payload.studentAnswer || !payload.evaluationResult)) {
    return {
      success: false,
      message: 'Dados da resposta e avaliação são obrigatórios.',
    };
  }

  const body = payload.provaId
    ? {
        prova_id: payload.provaId,
        disagreement_reason: payload.disagreementReason,
      }
    : {
        disagreement_reason: payload.disagreementReason,
        student_answer: payload.studentAnswer!,
        ai_verdict: payload.evaluationResult!.verdict || '',
        ai_justification: payload.evaluationResult!.feedback || '',
        ai_score: payload.evaluationResult!.score ?? 0,
        evaluation_method: payload.evaluationResult!.evaluationMethod ?? 'ai',
      };

  try {
    const response = await request(
      `/v2/students/questions/${payload.questionId}/disagree`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
    return {
      success: true,
      message: response?.message || DISAGREE_API_MESSAGES.SUCCESS,
    };
  } catch (error) {
    const apiError = error as Error & { status?: number };
    if (apiError.status === 409) {
      return {
        success: false,
        message: DISAGREE_API_MESSAGES.ALREADY_REGISTERED,
        alreadyExists: true,
      };
    }
    return {
      success: false,
      message: DISAGREE_API_MESSAGES.ERROR,
    };
  }
};

export const evaluateContextAnswer = async (
  questionId: number,
  answer: string,
): Promise<EvaluationResult> => {
  const response = await request(`/v2/students/exams/evaluate-context-answer`, {
    method: 'POST',
    body: JSON.stringify({
      question_id: questionId,
      student_answer: answer,
    }),
  });

  const responseApi = response as {
    question_id: number;
    student_answer: string;
    is_correct: boolean;
    nota: number;
    verdict: string;
    justification: string;
    evaluation_method: string;
  };

  return {
    score: responseApi.nota,
    maxScore: 100,
    feedback: responseApi.justification,
    evaluatedAt: new Date().toISOString(),
    isCorrect: responseApi.is_correct,
    verdict: responseApi.verdict,
    evaluationMethod: responseApi.evaluation_method,
  };
};
